import { useCallback, useMemo, useState } from 'react'
import { CheckCheck, Sparkles, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { AiActionCard } from './AiActionCard'
import {
  AiActionConflictError,
  AiActionPermissionError,
  type AiActionHandlers,
  type AiActionState,
  type AiActionStatus,
  type AiSuggestionAction,
} from './types'

export interface AiActionTrayProps extends AiActionHandlers {
  actions: readonly AiSuggestionAction[]
  /**
   * Initial states per action id. Defaults to `pending` when omitted.
   * The tray manages state internally after mount; this is purely the
   * seed — useful for tests and for restoring a saved review session.
   */
  initialStates?: Readonly<Record<string, AiActionState>>
  title?: string
  description?: string
  /** Hide the batch picker / "Apply N" controls. */
  hideBatch?: boolean
  emptyState?: React.ReactNode
  /** Optional callback fired when an action transitions to a new status. */
  onStateChange?: (id: string, state: AiActionState) => void
  className?: string
}

const TERMINAL: ReadonlySet<AiActionStatus> = new Set([
  'applied',
  'rejected',
])

/**
 * Stateful review tray for a batch of AI-suggested actions. The tray:
 *  - renders one `AiActionCard` per action with explicit Apply / Reject
 *  - manages per-action selection for batch apply / batch reject
 *  - serialises batch operations (apply N) so per-card error state stays
 *    coherent if one fails midway through
 *  - maps `AiActionConflictError` → `conflict` status,
 *    `AiActionPermissionError` → `denied`, anything else → `failed`
 *
 * Nothing in this tray mutates document or project state on its own —
 * every transition is gated by an explicit user click on a control
 * inside the tray. This is the "safety boundary" the parent review
 * called out as missing from the legacy chat SSE flow.
 */
export function AiActionTray({
  actions,
  initialStates,
  title = 'AI suggestions',
  description = 'Review each suggestion before applying. Nothing changes until you click Apply.',
  hideBatch = false,
  emptyState,
  onApply,
  onReject,
  onForceApply,
  onStateChange,
  className,
}: AiActionTrayProps) {
  const [states, setStates] = useState<Record<string, AiActionState>>(
    () => seedStates(actions, initialStates),
  )
  const [selected, setSelected] = useState<Record<string, boolean>>({})
  const [batchInFlight, setBatchInFlight] = useState(false)

  const reviewable = useMemo(
    () => actions.filter((a) => !TERMINAL.has(states[a.id]?.status ?? 'pending')),
    [actions, states],
  )

  const pendingCount = reviewable.filter((a) => {
    const status = states[a.id]?.status ?? 'pending'
    return status === 'pending' || status === 'conflict' || status === 'failed'
  }).length

  const setActionState = useCallback(
    (id: string, next: AiActionState) => {
      setStates((prev) => ({ ...prev, [id]: next }))
      onStateChange?.(id, next)
    },
    [onStateChange],
  )

  const selectableActions = useMemo(
    () =>
      reviewable.filter(
        (a) => (states[a.id]?.status ?? 'pending') === 'pending',
      ),
    [reviewable, states],
  )

  const selectedActions = useMemo(
    () => selectableActions.filter((a) => selected[a.id]),
    [selectableActions, selected],
  )

  const allSelected =
    selectableActions.length > 0 &&
    selectableActions.every((a) => selected[a.id])

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelected({})
      return
    }
    const next: Record<string, boolean> = {}
    for (const a of selectableActions) next[a.id] = true
    setSelected(next)
  }

  const toggleSelected = (id: string) => {
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  const runApply = useCallback(
    async (
      action: AiSuggestionAction,
      fn: (a: AiSuggestionAction) => Promise<void>,
    ) => {
      setActionState(action.id, { status: 'applying' })
      try {
        await fn(action)
        setActionState(action.id, { status: 'applied' })
        setSelected((prev) => {
          if (!prev[action.id]) return prev
          const next = { ...prev }
          delete next[action.id]
          return next
        })
      } catch (err) {
        const next = errorToState(err)
        setActionState(action.id, next)
      }
    },
    [setActionState],
  )

  const applyOne = useCallback(
    (action: AiSuggestionAction) => runApply(action, onApply),
    [onApply, runApply],
  )

  // Force-apply is only used by the conflict panel's "Apply anyway".
  // Without a dedicated handler the button would just re-trigger the
  // same conflict-checked path and bounce the card right back to
  // `conflict`, so callers that gate apply on a conflict check MUST
  // wire `onForceApply` for the button to mean anything.
  const forceApplyOne = useCallback(
    (action: AiSuggestionAction) => runApply(action, onForceApply ?? onApply),
    [onApply, onForceApply, runApply],
  )

  const rejectOne = useCallback(
    async (action: AiSuggestionAction) => {
      // Reject is a local decision in most flows, but we still let the
      // caller intercept it (e.g. to clear a server-side suggested_value).
      try {
        await onReject(action)
      } catch (err) {
        setActionState(action.id, errorToState(err))
        return
      }
      setActionState(action.id, { status: 'rejected' })
      setSelected((prev) => {
        if (!prev[action.id]) return prev
        const next = { ...prev }
        delete next[action.id]
        return next
      })
    },
    [onReject, setActionState],
  )

  const applyBatch = async () => {
    if (selectedActions.length === 0 || batchInFlight) return
    setBatchInFlight(true)
    // Serialise so per-card status flips one-at-a-time and a failure in
    // the middle leaves the rest in a coherent state (the still-pending
    // cards stay reviewable, the failed card shows Retry).
    for (const action of selectedActions) {
      await applyOne(action)
    }
    setBatchInFlight(false)
  }

  const rejectBatch = async () => {
    if (selectedActions.length === 0 || batchInFlight) return
    setBatchInFlight(true)
    for (const action of selectedActions) {
      await rejectOne(action)
    }
    setBatchInFlight(false)
  }

  if (actions.length === 0) {
    return (
      <section
        className={cn('rounded-lg border bg-card p-4', className)}
        data-testid="ai-action-tray-empty"
      >
        {emptyState ?? (
          <p className="text-sm text-muted-foreground">
            No AI suggestions to review.
          </p>
        )}
      </section>
    )
  }

  return (
    <section
      className={cn('space-y-3 rounded-lg border bg-card p-4', className)}
      data-testid="ai-action-tray"
      data-pending-count={pendingCount}
    >
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="flex items-center gap-1.5 text-sm font-semibold tracking-tight text-foreground">
            <Sparkles className="size-3.5" aria-hidden />
            {title}
            <span
              className="ml-1 inline-flex items-center rounded-full bg-warning-soft px-1.5 py-0.5 text-[10px] font-medium text-warning-soft-foreground"
              data-testid="ai-action-tray-count"
            >
              {pendingCount} pending
            </span>
          </h3>
          <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
            {description}
          </p>
        </div>
        {!hideBatch && selectableActions.length > 0 && (
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleSelectAll}
                className="size-4 cursor-pointer rounded border-input accent-primary"
                data-testid="ai-action-select-all"
                aria-label="Select all pending suggestions"
              />
              Select all ({selectableActions.length})
            </label>
          </div>
        )}
      </header>

      {!hideBatch && selectedActions.length > 0 && (
        <div
          className="flex flex-wrap items-center gap-2 rounded-md border border-primary/30 bg-primary-soft/40 px-3 py-2 text-xs"
          data-testid="ai-action-batch-bar"
        >
          <span className="font-medium text-foreground">
            {selectedActions.length} selected
          </span>
          <div className="ml-auto flex gap-1.5">
            <Button
              size="sm"
              variant="default"
              onClick={applyBatch}
              disabled={batchInFlight}
              data-testid="ai-action-batch-apply"
            >
              <CheckCheck />
              Apply {selectedActions.length}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={rejectBatch}
              disabled={batchInFlight}
              data-testid="ai-action-batch-reject"
            >
              <X />
              Reject {selectedActions.length}
            </Button>
          </div>
        </div>
      )}

      <ul className="space-y-2" data-testid="ai-action-tray-list">
        {actions.map((action) => {
          const state = states[action.id] ?? { status: 'pending' as const }
          return (
            <AiActionCard
              key={action.id}
              action={action}
              state={state}
              selected={Boolean(selected[action.id])}
              selectable={!batchInFlight}
              hideSelection={hideBatch}
              onToggleSelected={() => toggleSelected(action.id)}
              onAccept={() => void applyOne(action)}
              onReject={() => void rejectOne(action)}
              onForceAccept={
                onForceApply ? () => void forceApplyOne(action) : undefined
              }
            />
          )
        })}
      </ul>
    </section>
  )
}

function seedStates(
  actions: readonly AiSuggestionAction[],
  initial: Readonly<Record<string, AiActionState>> | undefined,
): Record<string, AiActionState> {
  const out: Record<string, AiActionState> = {}
  for (const a of actions) {
    out[a.id] = initial?.[a.id] ?? { status: 'pending' }
  }
  return out
}

function errorToState(err: unknown): AiActionState {
  if (err instanceof AiActionConflictError) {
    return {
      status: 'conflict',
      message: err.message,
      conflictCurrent: err.currentValue,
    }
  }
  if (err instanceof AiActionPermissionError) {
    return { status: 'denied', message: err.message }
  }
  const message =
    err instanceof Error
      ? err.message
      : typeof err === 'string'
        ? err
        : 'Apply failed.'
  return { status: 'failed', message }
}
